import { DateHelper } from './date.helper';
import {
  NODE_STATUS_PASSED_LOWER,
  NODE_STATUS_UNDETERMINED_LOWER,
  NODE_STATUS_IN_PROGRESS_LOWER,
  NODE_STATUS_TO_DO_LOWER,
  NODE_STATUS_BLOCKED_LOWER,
  NODE_STATUS_FAILED_LOWER,
  NODE_STATUS_DONE_LOWER,
} from './../../modules/catch-core/data/constants/app.constants';
import { ITestStepInstanceModel, TestStepCollection, TestStepInstanceModel } from './../../models/test-step/test-step-instance.model';
import { IdHelper } from './id.helper';
import { CatchHubLogStore } from './../stores/catchHubLog.store';
import { ProjectStore } from './../stores/projects.store';
import { AlertService } from './../alert.service';
import { UserStore } from './../stores/user.store';
import { UserDataHelper } from './user-data.helper';
import { NodeHelper } from './node.helper';
import { WorkspaceService } from '../workspace.service';
import { Injectable } from '@angular/core';
import { PatchNodeInstancePayload } from '../../models/node/patch-node-instance-payload.model';
import * as XLSX from 'xlsx';
import { INodeInstanceModel, NodeInstanceModel } from '../../models/node/node-instance.model';
import { BlueprintDataHelper } from './blueprint-data.helper';
import { WorkspaceStore } from '../stores/workspace.store';
import { concat, concatMap, finalize, from, map, Observable, of } from 'rxjs';
import { ALLNODE, IMPORT_EVENTS, TESTNODEONLY } from '../../modules/catch-core/data/constants/import.constants';
import {
  TEST_NODE_LOWER,
  FIELD_TESTING_TABLE,
  TEST_STATUS_FIELD_LOWER,
  STARTDATEFIELDNAME,
  DUEDATEFIELDNAME,
  PRIORITY_FIELD_LOWER,
  ASSIGNED_TO_FIELD,
  IMPORT_CSV_HEADER_TEST_SUMMARY_LOWER,
  IMPORT_CSV_HEADER_TEST_STEP_ID_LOWER,
  IMPORT_CSV_HEADER_TEST_STEP_DESCRIPTION_LOWER,
  IMPORT_CSV_HEADER_TYPE_LOWER,
  IMPORT_CSV_HEADER_SUMMARY_LOWER,
  IMPORT_CSV_HEADER_TEST_DESCRIPTION_LOWER,
  IMPORT_CSV_HEADER_TEST_PRIORITY_LOWER,
  IMPORT_CSV_HEADER_TEST_ASSIGNEE_LOWER,
  IMPORT_CSV_HEADER_TEST_START_DATE_LOWER,
  IMPORT_CSV_HEADER_TEST_DUE_DATE_LOWER,
  IMPORT_CSV_HEADER_TEST_STEP_EXPECTED_RESULT_LOWER,
  IMPORT_CSV_HEADER_TEST_STEP_DATA_LOWER,
  IMPORT_CSV_HEADER_TEST_STEP_NOTES_LOWER,
  IMPORT_CSV_HEADER_TEST_STEP_ACTUAL_RESULT_LOWER,
  IMPORT_CSV_HEADER_TEST_STEP_STATUS_LOWER,
  IMPORT_CSV_HEADER_DESCRIPTION_LOWER,
  IMPORT_CSV_HEADER_STATUS_LOWER,
  FIELD_NAME_START_DATE,
  FIELD_NAME_DUE_DATE,
  FIELD_NAME_ASSIGNEE_TITLE,
  FIELD_NAME_PRIORITY_TITLE,
  IMPORT_MANDATORY_FIELD_SUMMARY_TITLE,
  IMPORT_MANDATORY_FIELD_TEST_STEP_DESCRIPTION_TITLE,
  IMPORT_SUMMARY_MAX_LENGTH,
  IMPORT_DONOT_SET_LOWER,
  IMPORT_LOG_SOURCE,
  IMPORT_LOG_LEVEL_INFO_LOWER,
  IMPORT_UNKNOWN_USER,
  IMPORT_LOG_RUN_TAG,
  IMPORT_LOG_ITEM_NOUN_ALL_NODE,
  IMPORT_LOG_ITEM_NOUN_TEST_NODE,
  IMPORT_CONTEXT_TEST_NODE,
  IMPORT_CONTEXT_ALL_NODE,
  IMPORT_LIMIT_MESSAGE,
  IMPORT_FAILURE_REASON_UNEXPECTED_ERROR,
  IMPORT_FAILURE_REASON_FILE_READ,
  IMPORT_FAILURE_REASON_NO_DATA_ROWS,
  IMPORT_FAILURE_REASON_UNSUPPORTED_FILE_TYPE,
  IMPORT_LOG_LABEL_SEPARATOR_SLASH,
  IMPORT_LOG_LABEL_SEPARATOR_HYPHEN,
  FIELD_CATEGORY_STATUS_LOWER,
  FILE_EXTENSION_XLS_LOWER,
  FILE_EXTENSION_XLSX_LOWER,
  FILE_EXTENSION_CSV_LOWER,
} from '../../modules/catch-core/data/constants/blueprint.constants';
import * as FilterConstants from '../../modules/catch-core/data/constants/filter.constants';
import { EditPanelTestStepHelperService } from '../../modules/shared-edit-panel/services/edit-panel-test-step.helper';

import { CatchHubLogModel } from './../../models/catchHubLog/catchHubLog.model';
import { NotificationType } from 'angular2-notifications';

@Injectable({ providedIn: 'root' })
export class ImportHelper {
  private logModelList: CatchHubLogModel[] = [];
  private mode: string = '';
  private skipRowCount = 0;
  private importRunTag = '';
  constructor(
    private bluePrintDataHelper: BlueprintDataHelper,
    private workspaceStore: WorkspaceStore,
    private workspaceService: WorkspaceService,
    private nodeHelper: NodeHelper,
    private userDataHelper: UserDataHelper,
    private userStore: UserStore,
    private alertService: AlertService,
    private projectStore: ProjectStore,
    private catchHubLogStore: CatchHubLogStore,
    private idHelper: IdHelper,
    private dateHelper: DateHelper,
    private editPanelTestStepHelper: EditPanelTestStepHelperService,
  ) { }

  public importCSVHandler(file: File | null | undefined, mode: string): Observable<{ type: string; success: boolean; completed: number; total: number }> {
    // reset values
    this.logModelList = [];
    this.mode = mode?.toLocaleLowerCase() ?? '';
    this.importRunTag = '';

    const defaultResult = {
      type: IMPORT_EVENTS.IMPORT_EVENT_TYPE_DONE_LOWER,
      success: false,
      completed: 0,
      total: 0,
    };

    if (!file) {
      return of(defaultResult);
    }

    this.importRunTag = `[${IMPORT_LOG_RUN_TAG} ${file.name ?? ''} ${this.dateHelper.formatDate(new Date().toISOString())}] `;

    const fileName = (file.name ?? '').toLowerCase();
    const isExcel = fileName.endsWith(FILE_EXTENSION_XLS_LOWER) || fileName.endsWith(FILE_EXTENSION_XLSX_LOWER);
    const isCSV = fileName.endsWith(FILE_EXTENSION_CSV_LOWER);

    if (!isExcel && !isCSV) {
      this.addLogsToStore(this.buildImportFailureSummary(0, IMPORT_FAILURE_REASON_UNSUPPORTED_FILE_TYPE));
      return of(defaultResult);
    }

    return new Observable((observer) => {
      const reader = new FileReader();
      this.skipRowCount = 0;

      reader.onload = (e) => {
        try {
          const content = e?.target?.result;
          if (typeof content !== 'string') {
            this.addLogsToStore(this.buildImportFailureSummary(0, IMPORT_FAILURE_REASON_FILE_READ));
            observer.next(defaultResult);
            observer.complete();
            return;
          }

          const rows = this.extractRowsFromCSV(content, isExcel);
          if (!rows || rows.length < 2) {
            this.addLogsToStore(this.buildImportFailureSummary(0, IMPORT_FAILURE_REASON_NO_DATA_ROWS));
            observer.next(defaultResult);
            observer.complete();
            return;
          }
          let validationError: string | null = null;

          if (mode === ALLNODE.toLowerCase()) {
            validationError = this.validationAllNodeImport(rows);
          } else if (mode === TESTNODEONLY.toLowerCase()) {
            validationError = this.validationTestNodeWithSteps(rows);
          }

          if (validationError) {
            this.addLogsToStore(this.buildImportFailureSummary(0, validationError));
            observer.next(defaultResult);
            observer.complete();
            return;
          }

          const patchPayloads = this.buildPatchPayloads(rows, mode);
          if (!patchPayloads?.length) {
            // Every row was skipped (e.g. all missing a mandatory field) - the skip reasons
            // were logged per-row via addLogMessage, but without this they'd never reach the
            // audit log, since pushLogToAmoeba/addLogsToStore otherwise only run after a
            // successful patchNodes completion.
            this.pushLogToAmoeba(patchPayloads?.length ?? 0, this.skipRowCount);
            observer.next(defaultResult);
            observer.complete();
            return;
          }

          if (patchPayloads.length > 1000) {
            this.alertService.alert(NotificationType.Warn, IMPORT_LIMIT_MESSAGE, true);
            this.addLogsToStore(this.buildImportFailureSummary(0, `${IMPORT_LIMIT_MESSAGE} ${patchPayloads.length} found in the file.`));
            observer.next(defaultResult);
            observer.complete();
            return;
          }

          let completedCount = 0;

          // Call batched patchNodes
          this.patchNodes(patchPayloads).subscribe({
            next: (progress) => {
              completedCount = progress?.completed ?? completedCount;
              observer.next({
                type: IMPORT_EVENTS.IMPORT_EVENT_TYPE_PROGRESS_LOWER,
                completed: progress?.completed ?? 0,
                total: progress?.total ?? patchPayloads.length,
                success: true,
              });
            },
            error: () => {
              // Batches already saved before the failing one stay imported - report them.
              this.addLogsToStore(this.buildImportFailureSummary(completedCount, `An error occurred while saving - ${patchPayloads.length - completedCount} not imported.`));
              observer.next(defaultResult);
              observer.complete();
            },
            complete: () => {
              // only called if no error occurred
              this.pushLogToAmoeba(patchPayloads.length, this.skipRowCount);
              observer.next({
                type: IMPORT_EVENTS.IMPORT_EVENT_TYPE_DONE_LOWER,
                success: true,
                completed: patchPayloads.length,
                total: patchPayloads.length,
              });
              observer.complete();
            },
          });
        } catch (error) {
          // Only synchronous work runs in this try - saving happens in patchNodes' subscription,
          // whose failures go to its own error handler - so nothing has been imported yet.
          this.addLogsToStore(this.buildImportFailureSummary(0, IMPORT_FAILURE_REASON_UNEXPECTED_ERROR));
          observer.next(defaultResult);
          observer.complete();
        }
      };

      reader.onerror = () => {
        this.addLogsToStore(this.buildImportFailureSummary(0, IMPORT_FAILURE_REASON_FILE_READ));
        observer.next(defaultResult);
        observer.complete();
      };

      isExcel ? reader.readAsBinaryString(file) : reader.readAsText(file);
    });
  }

  // Validations for CSV headers. Returns null when valid, or the failure reason otherwise -
  // the reason flows straight into the "Import action failed..." summary instead of a generic message.
  private validationTestNodeWithSteps(rows: any[][]): string | null {
    // Test Summary was renamed to Summary in Export - accept either so files from the
    // current Export still pass validation.
    const requiredHeaders = [
      [IMPORT_CSV_HEADER_TEST_SUMMARY_LOWER, IMPORT_CSV_HEADER_SUMMARY_LOWER],
      IMPORT_CSV_HEADER_TEST_STEP_ID_LOWER,
      IMPORT_CSV_HEADER_TEST_STEP_DESCRIPTION_LOWER,
    ];
    return this.validateRequiredHeaders(rows, requiredHeaders, IMPORT_CONTEXT_TEST_NODE);
  }

  private validationAllNodeImport(rows: any[][]): string | null {
    const requiredHeaders = [IMPORT_CSV_HEADER_TYPE_LOWER, IMPORT_CSV_HEADER_SUMMARY_LOWER];
    return this.validateRequiredHeaders(rows, requiredHeaders, IMPORT_CONTEXT_ALL_NODE);
  }

  private validateRequiredHeaders(rows: any[][], requiredHeaders: (string | string[])[], context: string): string | null {
    if (!rows || rows.length === 0) {
      return `${context}: No rows found in the file.`;
    }

    const [headers] = rows;
    if (!headers || headers.length < 2) {
      return `${context}: CSV file must contain at least two columns.`;
    }

    const lowerCaseHeaders = headers.map((h) => h?.toString().toLowerCase?.() ?? '');

    for (const required of requiredHeaders) {
      const alternates = Array.isArray(required) ? required : [required];
      if (!alternates.some((alt) => lowerCaseHeaders.includes(alt))) {
        return `${context}: Missing required header: ${alternates.join(' / ')}`;
      }
    }

    return null;
  }

  private extractRowsFromCSV(content: string | null | undefined, isBinary: boolean): (string | Date | null)[][] | null {
    if (!content || typeof content !== 'string') {
      //console.error('Invalid or missing file content.');
      //this.alertService.showErrorMessage('Invalid or missing file content.');
      this.addLogMessage('Invalid or missing file content.');
      return null;
    }

    try {
      const workbook = XLSX.read(content, {
        type: isBinary ? 'binary' : 'string',
        raw: false,
      });

      const sheetName = workbook.SheetNames?.[0];
      const sheet = sheetName ? workbook.Sheets?.[sheetName] : null;

      if (!sheet) {
        //console.error('Worksheet not found.');
        //this.alertService.showErrorMessage('Worksheet not found.');
        this.addLogMessage('Worksheet not found.');
        return null;
      }

      const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      return rows.length >= 2 ? this.postProcessDateStrings(rows) : rows;
    } catch (error) {
      //console.error('Error parsing content:', error);
      this.addLogMessage('Error parsing content. Please check the file format.');
      //this.alertService.showErrorMessage('Error parsing content. Please check the file format.');
      return null;
    }
  }

  private buildPatchPayloads(rows: any[][], mode: string): PatchNodeInstancePayload[] | null {
    switch (mode) {
      case ALLNODE:
        return this.buildAllNodePatchPayloads(rows);
      case TESTNODEONLY:
        return this.buildTestNodeWithStepsPatchPayloads(rows);

      default:
        //console.warn(`Unknown import mode: ${mode}`);
        this.alertService.showErrorMessage(`Unknown import mode: ${mode}`);
        return null;
    }
  }

  private postProcessDateStrings(rows: any[][] | null | undefined): (string | Date | null)[][] {
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    const headers = rows[0];
    if (!headers || !Array.isArray(headers)) {
      return rows;
    }

    const dataRows = rows.slice(1);
    const startDateHeaders = [FIELD_NAME_START_DATE.toLowerCase(), IMPORT_CSV_HEADER_TEST_START_DATE_LOWER];
    const dueDateHeaders = [FIELD_NAME_DUE_DATE.toLowerCase(), IMPORT_CSV_HEADER_TEST_DUE_DATE_LOWER];
    const startDateIndex = headers.findIndex((h) => typeof h === 'string' && startDateHeaders.includes(h.toLowerCase()));
    const dueDateIndex = headers.findIndex((h) => typeof h === 'string' && dueDateHeaders.includes(h.toLowerCase()));

    return [
      headers,
      ...dataRows.map((row, rowIndex) => {
        if (!Array.isArray(row)) return row;

        const newRow = [...row];

        if (startDateIndex >= 0 && newRow[startDateIndex] != null) {
          newRow[startDateIndex] = this.tryParseDate(newRow[startDateIndex], rowIndex);
        }

        if (dueDateIndex >= 0 && newRow[dueDateIndex] != null) {
          newRow[dueDateIndex] = this.tryParseDate(newRow[dueDateIndex], rowIndex);
        }

        return newRow;
      }),
    ];
  }

  private isPlausibleImportYear(date: Date | null | undefined): boolean {
    if (!date) return false;
    const year = date.getFullYear();
    return year >= 1900 && year <= new Date().getFullYear() + 50;
  }

  private tryParseDate(val: any, rowIndex?: number): Date | any {
    if (!val) return val; // Return null or undefined as is
    const parsed = typeof val === 'number' ? this.convertExcelDateSerial(val) : this.parseDateValue(val);
    if (isNaN(parsed.getTime())) return val;
    if (!this.isPlausibleImportYear(parsed)) {
      const reason = `Date value "${val}" parsed to an implausible year (${parsed.getFullYear()}) - date not set`;
      this.addLogMessage(rowIndex != null ? this.formatRowIssue(rowIndex + 2, reason) : reason);
      return null;
    }
    return parsed;
  }

  private convertExcelDateSerial(serial: number): Date {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    return new Date(excelEpoch.getTime() + serial * 86400000); // 24*60*60*1000
  }

  // Log format for a Date - d/M/yyyy to match the user's dd/MM/yyyy input; Date.prototype.toString()
  // is TZ/locale-dependent and unsearchable.
  private formatDateForLog(date: Date): string {
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  }

  private buildDateRangeReason(startDate: Date, dueDate: Date): string {
    return `Start date ${this.formatDateForLog(startDate)} cannot be after due date ${this.formatDateForLog(dueDate)} - Dates are not set`;
  }

  private buildTestNodeWithStepsPatchPayloads(rows: any[][]): PatchNodeInstancePayload[] {
    if (!rows) return [];

    const [headers, ...dataRows] = rows;
    const typeId = this.getNodeTypeId(TEST_NODE_LOWER);
    if (!headers || !typeId) return [];

    const payloads: PatchNodeInstancePayload[] = [];
    let node: INodeInstanceModel | null = null;
    let testSteps: ITestStepInstanceModel[] = [];
    let csvTestNodeStatus = '';

    this.skipRowCount = 0;

    for (const [rowIndex, row] of dataRows.entries()) {
      try {
        const nodeObject = this.mapRowToNodeObject(headers, row);
        const testSummary = nodeObject[IMPORT_CSV_HEADER_TEST_SUMMARY_LOWER] ?? nodeObject[IMPORT_CSV_HEADER_SUMMARY_LOWER];

        // 🔹 If row has Test Summary → new test case starts
        if (testSummary) {
          // Flush previous node
          if (node) {
            this.addNodeToPayloads(payloads, node, testSteps, csvTestNodeStatus);
          }

          // Start new node
          node = this.createNodeInstance(typeId, this.truncateSummary(testSummary, rowIndex), false);
          if (!node) {
            this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Failed to create node for type: ${typeId}`, true));
            this.skipRowCount++;
            continue;
          }

          csvTestNodeStatus = nodeObject[TEST_STATUS_FIELD_LOWER] ?? nodeObject[IMPORT_CSV_HEADER_STATUS_LOWER] ?? '';
          node.description = nodeObject?.[IMPORT_CSV_HEADER_TEST_DESCRIPTION_LOWER] ?? nodeObject?.[IMPORT_CSV_HEADER_DESCRIPTION_LOWER] ?? '';
          this.setPriority(node, nodeObject[IMPORT_CSV_HEADER_TEST_PRIORITY_LOWER] ?? nodeObject[PRIORITY_FIELD_LOWER] ?? '');
          this.setAssignee(node, nodeObject[IMPORT_CSV_HEADER_TEST_ASSIGNEE_LOWER] ?? nodeObject[ASSIGNED_TO_FIELD] ?? '', rowIndex);

          let isDateRangeValid = true;

          let startDateStr = nodeObject[IMPORT_CSV_HEADER_TEST_START_DATE_LOWER] ?? nodeObject[STARTDATEFIELDNAME] ?? '';
          let dueDateStr = nodeObject[IMPORT_CSV_HEADER_TEST_DUE_DATE_LOWER] ?? nodeObject[DUEDATEFIELDNAME] ?? '';

          if (!startDateStr && !dueDateStr) {
            startDateStr = this.dateHelper.getToday();
            dueDateStr = this.dateHelper.getFutureDateByAddingMonths(1);
          }

          const startDate = this.parseDate(startDateStr);
          const dueDate = this.parseDate(dueDateStr);

          if (startDateStr && !startDate) {
            this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Start date "${startDateStr}" could not be parsed - not set`));
          }
          if (dueDateStr && !dueDate) {
            this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Due date "${dueDateStr}" could not be parsed - not set`));
          }

          if (startDate && dueDate && startDate > dueDate) {
            this.addLogMessage(this.formatRowIssue(rowIndex + 2, this.buildDateRangeReason(startDate, dueDate), true));
            isDateRangeValid = false;
          }

          this.setDates(isDateRangeValid, node, startDateStr, dueDateStr, startDate, dueDate);

          // Reset steps for new node
          testSteps = [];
        }

        // 🔹 If row has a step → attach to current node
        if (nodeObject[IMPORT_CSV_HEADER_TEST_STEP_ID_LOWER]) {
          this.addStepIfValid(testSteps, nodeObject, rowIndex);
        }
      } catch (err) {
        this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Failed to create node: ${err}`, true));
        this.skipRowCount++;
        continue;
      }
    }

    // 🔹 Flush last node
    if (node) {
      this.addNodeToPayloads(payloads, node, testSteps, csvTestNodeStatus);
    }

    //console.log(payloads);

    return payloads;
  }

  private buildAllNodePatchPayloads(rows: any[][]): PatchNodeInstancePayload[] {
    const [headers, ...dataRows] = rows;
    if (!headers) return [];
    this.skipRowCount = 0;

    const payloads: PatchNodeInstancePayload[] = [];

    for (const [rowIndex, row] of dataRows.entries()) {
      try {
        const nodeObject = this.mapRowToNodeObject(headers, row);
        const type = nodeObject?.type;
        const typeId = this.bluePrintDataHelper.getNodeTypeIdByName(type);

        if (!typeId) {
          //console.warn(`Invalid type: ${type}`);
          this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Invalid type: ${type} - Skipping row.`, true));
          this.skipRowCount++;
          continue;
        }

        const summary = nodeObject?.summary ?? '';
        const description = nodeObject?.description ?? '';
        const priority = nodeObject?.priority;
        const status = nodeObject?.status;
        let startDateStr = nodeObject?.[STARTDATEFIELDNAME];
        let dueDateStr = nodeObject?.[DUEDATEFIELDNAME];
        const assignee = nodeObject?.assignee ?? '';

        if (!summary) {
          this.addLogMessage(this.formatRowIssue(rowIndex + 2, `${IMPORT_MANDATORY_FIELD_SUMMARY_TITLE} is required - row skipped`, true));
          this.skipRowCount++;
          continue;
        }

        let isDueDateShouldBeSet = true;
        let isDateRangeValid = true;

        if (startDateStr && !dueDateStr) {
          isDueDateShouldBeSet = false;
        }

        if (!startDateStr && !dueDateStr) {
          startDateStr = this.dateHelper.getToday();
          dueDateStr = this.dateHelper.getFutureDateByAddingMonths(1);
          isDateRangeValid = true;
        }

        const startDate = this.parseDate(startDateStr);
        const dueDate = this.parseDate(dueDateStr);

        if (startDateStr && !startDate) {
          this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Start date "${startDateStr}" could not be parsed - not set`));
        }
        if (dueDateStr && !dueDate) {
          this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Due date "${dueDateStr}" could not be parsed - not set`));
        }

        if (startDate && dueDate && startDate > dueDate) {
          this.addLogMessage(this.formatRowIssue(rowIndex + 2, this.buildDateRangeReason(startDate, dueDate), true));
          isDateRangeValid = false;
        }

        const node = this.createNodeInstance(typeId, this.truncateSummary(summary, rowIndex), isDueDateShouldBeSet);
        if (!node) {
          //console.warn(`Failed to create node for type: ${type}`);
          this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Failed to create node for type: ${type}`, true));
          this.skipRowCount++;
          continue;
        }

        node.description = description;
        this.setPriority(node, priority);
        this.setStatus(node, type, status);

        if (type.toLowerCase() === TEST_NODE_LOWER) {
          this.setTestNodeStepStatus(type, node, status);
        }

        this.setAssignee(node, assignee, rowIndex);

        this.setDates(isDateRangeValid, node, startDateStr, dueDateStr, startDate, dueDate);

        payloads.push(this.createPatchPayload(node));
      } catch (err) {
        this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Failed to create node: ${err}`, true));
        //console.error('Error building patch payload for row:', row, err);
        this.skipRowCount++;
        continue;
      }
    }

    return payloads;
  }

  // Enforces the same Summary length limit as the UI - characters past the limit are dropped.
  private truncateSummary(summary: unknown, rowIndex: number): string {
    const summaryText = summary == null ? '' : String(summary);
    if (summaryText.length <= IMPORT_SUMMARY_MAX_LENGTH) {
      return summaryText;
    }

    this.addLogMessage(
      this.formatRowIssue(
        rowIndex + 2,
        `${IMPORT_MANDATORY_FIELD_SUMMARY_TITLE} is ${summaryText.length} characters, exceeding the ${IMPORT_SUMMARY_MAX_LENGTH}-character limit - truncated to the first ${IMPORT_SUMMARY_MAX_LENGTH} characters`,
      ),
    );
    return summaryText.substring(0, IMPORT_SUMMARY_MAX_LENGTH);
  }

  private setTestNodeStepStatus(type: string, node: INodeInstanceModel, status: string): void {
    if (!node || !type || type.toLowerCase() !== TEST_NODE_LOWER) {
      return;
    }

    const testTableField = this.editPanelTestStepHelper.getTestTableField(this.workspaceStore.selectedWorkspaceInstance);
    const testFieldId = testTableField?.id;
    if (!testFieldId) {
      return;
    }

    const config = this.editPanelTestStepHelper.buildTestStatusConfig(this.workspaceStore.selectedWorkspaceInstance);
    this.editPanelTestStepHelper.syncTestSteps(node, status, { id: testFieldId }, config);
  }



  //logs builder

  // importedCount is the number of items actually saved - skipped rows are already excluded from it.
  private pushLogToAmoeba(importedCount: number, skipCount: number): void {
    let summary = `${importedCount} ${this.getImportItemNoun()} successfully imported to the ${this.formatLabel(IMPORT_LOG_LABEL_SEPARATOR_SLASH)}`;
    if (skipCount > 0) {
      summary += ` - ${skipCount} row(s) not imported`;
    }
    this.addLogsToStore(summary);
  }

  // importedCount > 0 only for the partial-save-failure path (some batches already succeeded
  // before a later one failed) - every other failure reaches this with 0, so the "already
  // imported" clause is omitted rather than always reading "0 imported".
  private buildImportFailureSummary(importedCount: number, reason: string): string {
    const base = `Import action failed on ${this.formatLabel(IMPORT_LOG_LABEL_SEPARATOR_HYPHEN)} due to the reason: ${reason}`;
    return importedCount > 0 ? `${base} (${importedCount} ${this.getImportItemNoun()} already imported)` : base;
  }

  // Wraps a single row's invalid-data message with the label and row number, so each entry on
  // the admin Logs screen reads as a self-contained sentence instead of a bare field name.
  // isIgnored distinguishes a row/data that was dropped ("is ignored as ...") from one that was
  // imported with a fallback applied (e.g. assignee left unset), so the log doesn't overstate it.
  private formatRowIssue(rowNumber: number, reason: string, isIgnored = false): string {
    const prefix = `${this.formatLabel(IMPORT_LOG_LABEL_SEPARATOR_HYPHEN)} - From the import file, row #${rowNumber}`;
    return isIgnored ? `${prefix} is ignored as ${reason}` : `${prefix} - ${reason}`;
  }

  private getImportItemNoun(): string {
    return this.mode === ALLNODE.toLocaleLowerCase() ? IMPORT_LOG_ITEM_NOUN_ALL_NODE : IMPORT_LOG_ITEM_NOUN_TEST_NODE;
  }

  // Workspace label as name + key (never the raw internal id) joined by `separator`: ' / ' for the success
  // summary, '-' (the "Label Name-Key" form) for failure/row-issue log entries.
  private formatLabel(separator: string): string {
    const label = this.workspaceStore?.selectedWorkspaceInstance;
    return `${label?.name ?? ''}${separator}${label?.key || label?.id || ''}`;
  }

  /**
   * Persists this run's log entries with the summary on top. The admin Logs screen lists
   * newest-first by the server-stamped CreatedDate, and AddLogs inserts in list order - so the
   * summary is sent last (showing first) and the row-level messages are reversed so they still
   * read top-down in file-row order beneath it.
   */
  private addLogsToStore(summaryMessage: string): void {
    const rowLogs = [...this.logModelList];
    const summaryLog = this.createLogModel(summaryMessage);
    this.logModelList.push(summaryLog);
    this.catchHubLogStore.addLogs([...rowLogs.reverse(), summaryLog]);
  }

  private addLogMessage(message: string): void {
    this.logModelList.push(this.createLogModel(message));
  }

  private createLogModel(message: string): CatchHubLogModel {
    return new CatchHubLogModel(
      this.idHelper.generateNewGUID24(),
      `${this.importRunTag ?? ''}${message}`,
      new Date().toISOString(),
      new Date().toISOString(),
      this.userStore.logUser?.id || IMPORT_UNKNOWN_USER,
      this.userStore.logUser?.id || IMPORT_UNKNOWN_USER,
      this.projectStore?.selectedProject?.name ?? '',
      this.projectStore?.selectedProject?.id ?? '',
      IMPORT_LOG_SOURCE,
      '',
      '',
      IMPORT_LOG_LEVEL_INFO_LOWER,
      this.userStore.logUser?.emailAddress || IMPORT_UNKNOWN_USER,
    );
  }

  private parseDate(dateStr: string | Date | null | undefined): Date | null {
    if (!dateStr) return null;

    const parsed = this.parseDateValue(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  // Excel/CSV exports use the app's dd/MM/yyyy convention (see DateFormat.parse in date.helper.ts).
  // The native Date constructor assumes US MM/dd/yyyy for slash-delimited strings, which silently
  // misreads e.g. "03/10/2026" as 10 March instead of 3 October - defeating start<=due comparisons.
  private parseDateValue(val: string | Date): Date {
    if (val instanceof Date) return val;
    if (typeof val === 'string' && val.includes('/')) {
      const parts = val.split('/');
      if (parts.length !== 3) return new Date(NaN);
      const day = Number(parts[0]);
      const month = Number(parts[1]) - 1;
      const year = Number(parts[2]);
      if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
        return new Date(NaN);
      }
      const date = new Date(year, month, day);
      // The Date constructor doesn't validate ranges - out-of-range components (e.g. "99/99/2026",
      // or a real-looking but nonexistent date like "31/04/2026") silently roll over into a
      // different date instead of failing. Reject anything that doesn't round-trip exactly.
      if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
        return new Date(NaN);
      }
      return date;
    }
    return new Date(val);
  }

  private mapRowToNodeObject(headers: string[] | null | undefined, row: any[] | null | undefined): Record<string, any> {
    const obj: Record<string, any> = {};

    if (!headers || !row || !Array.isArray(headers) || !Array.isArray(row)) {
      //console.warn('Invalid headers or row input.');
      return obj;
    }

    headers.forEach((header, i) => {
      const key = typeof header === 'string' ? header.toLowerCase() : '';
      obj[key] = i < row.length ? row[i] ?? null : null;
    });

    return obj;
  }

  private setDates(isDateRangeValid: boolean, node: INodeInstanceModel, startDateStr: string, dueDateStr: string, startDate: Date | null, dueDate: Date | null): void {
    if (isDateRangeValid) {
      this.setNodeField(node, FIELD_NAME_START_DATE, startDateStr);
      this.setNodeField(node, FIELD_NAME_DUE_DATE, dueDateStr);
    } else {
      this.setNodeField(node, FIELD_NAME_START_DATE, IMPORT_DONOT_SET_LOWER);
      this.setNodeField(node, FIELD_NAME_DUE_DATE, IMPORT_DONOT_SET_LOWER);
    }

    if (!startDate) {
      this.setNodeField(node, FIELD_NAME_START_DATE, IMPORT_DONOT_SET_LOWER);
    }

    if (!dueDate) {
      this.setNodeField(node, FIELD_NAME_DUE_DATE, IMPORT_DONOT_SET_LOWER);
    }
  }

  private setAssignee(node: INodeInstanceModel, assignee: string, rowIndex: number) {
    if (!assignee) return;
    const user = this.userDataHelper.getUserIdByName(assignee, this.userStore.users.toArray());
    if (user) {
      this.setNodeField(node, FIELD_NAME_ASSIGNEE_TITLE, user);
    } else {
      this.addLogMessage(this.formatRowIssue(rowIndex + 2, `Assignee "${assignee}" is not matching with the Template values - Assignee is not set`));
    }
  }


  private setPriority(node: INodeInstanceModel, priority: string): void {
    const field = this.bluePrintDataHelper.getFieldByName(FIELD_NAME_PRIORITY_TITLE);
    if (!field?.id) return;

    const options = this.bluePrintDataHelper.getFieldOptionTexts(field);
    const priorityOptions = field?.value?.statusOptions ?? field?.value?.StatusOptions ?? [];
    const lowerPriority = priority?.toLowerCase();
    const matched = options?.find((v) => typeof v === 'string' && v.toLowerCase() === lowerPriority);

    const toStoredValue = (text: string): string => {
      if (!Array.isArray(priorityOptions) || priorityOptions.length === 0) return text;
      return this.bluePrintDataHelper.resolveStatusId(priorityOptions, text) || text;
    };

    if (matched) {
      node.fields[field.id] = toStoredValue(matched);
    } else {
      this.addLogMessage(`Priority option "${priority}" is not matching with the Template values - Priority set to "${options?.[0] ?? ''}"`);
      if (options?.[0]) {
        node.fields[field.id] = toStoredValue(options[0]);
      }
    }
  }

  private setStatus(node: INodeInstanceModel, type: string, status: string): void {
    const field = type.toLowerCase() === TEST_NODE_LOWER
      ? this.bluePrintDataHelper.getFieldByName(TEST_STATUS_FIELD_LOWER)
      : this.bluePrintDataHelper.findFieldByCategory(type, FIELD_CATEGORY_STATUS_LOWER);
    if (!field?.id) return;

    const options = this.bluePrintDataHelper.getFieldOptionTexts(field);
    const statusOptions = field?.value?.statusOptions ?? field?.value?.StatusOptions ?? [];
    const lowerStatus = status?.toLowerCase();
    const matchedText = options?.find((v) => typeof v === 'string' && v.toLowerCase() === lowerStatus);

    const toStoredValue = (text: string): string => {
      if (!Array.isArray(statusOptions) || statusOptions.length === 0) return text;
      return this.bluePrintDataHelper.resolveStatusId(statusOptions, text) || text;
    };

    if (matchedText) {
      node.fields[field.id] = toStoredValue(matchedText);
    } else {
      const defaultStatus = options?.find((v) => typeof v === 'string' && v.toLowerCase() === FilterConstants.STATUS_TO_DO.toLowerCase()) ?? options?.[0];
      this.addLogMessage(`Status option "${status}" is not matching with the Template values - Status set to "${defaultStatus ?? ''}"`);
      if (defaultStatus) {
        node.fields[field.id] = toStoredValue(defaultStatus);
      }
    }
  }

  private buildTestStep(data: Record<string, any>): ITestStepInstanceModel {
    const step = new TestStepInstanceModel();
    step.id = data[IMPORT_CSV_HEADER_TEST_STEP_ID_LOWER] ?? 0;
    step.stepId = data[IMPORT_CSV_HEADER_TEST_STEP_ID_LOWER] ?? 0;
    step.description = data[IMPORT_CSV_HEADER_TEST_STEP_DESCRIPTION_LOWER] ?? '';
    step.expectedResult = data[IMPORT_CSV_HEADER_TEST_STEP_EXPECTED_RESULT_LOWER] ?? '';
    step.data = data[IMPORT_CSV_HEADER_TEST_STEP_DATA_LOWER] ?? '';
    step.notes = data[IMPORT_CSV_HEADER_TEST_STEP_NOTES_LOWER] ?? '';
    step.actualResult = data[IMPORT_CSV_HEADER_TEST_STEP_ACTUAL_RESULT_LOWER] ?? '';
    step.attachments = [];
    step.teststatus = this.resolveStepStatus(data[IMPORT_CSV_HEADER_TEST_STEP_STATUS_LOWER]);
    return step;
  }

  private resolveStepStatus(status: string): string {
    if (!status) return FilterConstants.STATUS_TO_DO;
    const validOptions = this.editPanelTestStepHelper.getTestStatusOptions(this.workspaceStore.selectedWorkspaceInstance);
    const match = validOptions?.find((o) => o.toLowerCase() === status.toLowerCase());
    return match ?? FilterConstants.STATUS_TO_DO;
  }

  private addStepIfValid(testSteps: ITestStepInstanceModel[], data: Record<string, any>, rowIndex: number): void {
    if (!data[IMPORT_CSV_HEADER_TEST_STEP_DESCRIPTION_LOWER]) {
      this.addLogMessage(this.formatRowIssue(rowIndex + 2, `${IMPORT_MANDATORY_FIELD_TEST_STEP_DESCRIPTION_TITLE} is required - step skipped`, true));
      this.skipRowCount++;
      return;
    }
    const step = this.buildTestStep(data);
    testSteps.push(step);
  }

  private addNodeToPayloads(payloads: PatchNodeInstancePayload[], node: INodeInstanceModel, steps: ITestStepInstanceModel[], csvStatus: string): void {
    const json = JSON.stringify(steps);
    const calculatedStatus = this.setTestNodeStatusBasedOnStepsStatus(steps);

    const field = this.bluePrintDataHelper.getFieldByName(TEST_STATUS_FIELD_LOWER);
    const options = field ? this.bluePrintDataHelper.getFieldOptionTexts(field) : [];
    const isCsvStatusValid = !!options?.find((v) => typeof v === 'string' && v.toLowerCase() === csvStatus.toLowerCase());

    if (csvStatus) {
      if (!isCsvStatusValid) {
        this.addLogMessage(`Status option "${csvStatus}" is not matching with the Template values`);
      } else if (csvStatus.toLowerCase() !== calculatedStatus.toLowerCase()) {
        this.addLogMessage(`The Test node status differs from the status "${csvStatus}" calculated from its steps and has been replaced with the calculated value "${calculatedStatus}"`);
      }
    }

    this.setStatus(node, TEST_NODE_LOWER, calculatedStatus);
    this.setNodeField(node, FIELD_TESTING_TABLE, json);
    payloads.push(this.createPatchPayload(node));
  }

  private setTestNodeStatusBasedOnStepsStatus(steps: ITestStepInstanceModel[]): string {
    if (!steps || steps.length === 0) {
      return FilterConstants.STATUS_TO_DO; // default if no steps
    }

    const statusList = steps.map((step) => step.teststatus?.toLowerCase() ?? '');

    // 1. Blocked has the highest priority
    if (statusList.includes(NODE_STATUS_BLOCKED_LOWER)) {
      return FilterConstants.STATUS_BLOCKED;
    }

    // 2. Failed (only if no blocked)
    if (statusList.includes(NODE_STATUS_FAILED_LOWER)) {
      return FilterConstants.STATUS_FAILED;
    }

    // 3. Any Undetermined
    if (statusList.includes(NODE_STATUS_UNDETERMINED_LOWER)) {
      return FilterConstants.STATUS_UNDETERMINED;
    }

    // 4. All To Do
    if (statusList.every((s) => s === NODE_STATUS_TO_DO_LOWER)) {
      return FilterConstants.STATUS_TO_DO;
    }

    // 5. In Progress (mixed To Do + something else)
    if (statusList.includes(NODE_STATUS_TO_DO_LOWER)) {
      return FilterConstants.STATUS_IN_PROGRESS;
    }

    // 6. All Done (passed or done)
    if (statusList.every((s) => s === NODE_STATUS_PASSED_LOWER || s === NODE_STATUS_DONE_LOWER)) {
      return FilterConstants.STATUS_DONE;
    }

    // 7. Fallback
    return FilterConstants.STATUS_TO_DO;
  }

  private setNodeField(node: NodeInstanceModel, fieldName: string, value: any): void {
    if (!fieldName || value == null) return;
    const fieldId = this.bluePrintDataHelper.getFieldByName(fieldName)?.id;
    if (fieldId) node.fields[fieldId] = value;
  }

  private createNodeInstance(typeId: string, summary: string, isDueDateSHouldBeSet = true): INodeInstanceModel | null {
    const node = this.nodeHelper.getNewNodeInstance(typeId, summary ?? '', '-1 -1', isDueDateSHouldBeSet);
    //if (!node) console.warn('Failed to create node.');
    return node;
  }

  private getNodeTypeId(name: string): string | null {
    const typeId = this.bluePrintDataHelper.getNodeTypeIdByName(name);
    //if (!typeId) console.warn('Invalid node type:', name);
    return typeId;
  }

  private createPatchPayload(node: INodeInstanceModel): PatchNodeInstancePayload {
    return {
      workspaceId: this.workspaceStore.selectedWorkspaceInstance?.id ?? '',
      nodeInstanceItem: node,
      updateResponse: true,
      metaData: {},
    };
  }

  private patchNodes(patchList: PatchNodeInstancePayload[], batchSize: number = 10): Observable<{ completed: number; total: number }> {
    if (!patchList || patchList.length === 0) {
      return of({ completed: 0, total: 0 });
    }

    // Adjust batch size dynamically
    if (patchList.length >= 100 && patchList.length <= 1000) {
      batchSize = 100;
    }

    const workspaceId = this.workspaceStore.selectedWorkspaceInstance;

    // Split into batches
    const batches: PatchNodeInstancePayload[][] = [];
    for (let i = 0; i < patchList.length; i += batchSize) {
      batches.push(patchList.slice(i, i + batchSize));
    }

    const total = patchList.length;
    let completed = 0;

    return concat(
      // 👇 emit initial state before any batch runs
      of({ completed, total }),

      // then process batches
      from(batches).pipe(
        concatMap((batch) =>
          this.workspaceService.patchWSINodes(batch, workspaceId).pipe(
            map(() => {
              completed += batch.length;
              //console.log(`patch completed >> ${completed} / ${total}`);
              return { completed, total };
            }),
          ),
        ),
      ),
    ); //.pipe(finalize(() => console.log('✅ All batches processed')));
  }
}

function observerCompleteTrue(observer: any) {
  observer.next(true);
  observer.complete();
}

function observerCompleteFalse(observer: any) {
  observer.next(false);
  observer.complete();
}
